import { Flex, Popover, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { useContainerQuery } from 'src/UI/app-container/ContainerBreakpoint';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { BreadcrumbTerminalSlotFill } from './BreadcrumbTerminalSlot';
import { ClientSearch } from './ClientSearch';
import classes from './UnderlineTabs.module.css';
import { FONT_WEIGHTS } from '../theme/theme';

type ActiveMatch = 'exact' | 'startsWith' | 'includes';

type RouterTab = {
  label: string;
  href: string;
  activeMatch?: ActiveMatch;
  matchHref?: string;
};

type LocalTab = {
  label: string;
  value: string;
};

type BaseProps = {
  variant?: 'default' | 'bordered';
  showClientSearch?: boolean;
  integrateBreadcrumb?: boolean;
};

type Props = BaseProps &
  (
    | {
        mode: 'router';
        tabs: RouterTab[];
      }
    | {
        mode: 'local';
        tabs: LocalTab[];
        value: string;
        onChange: (value: string) => void;
      }
  );

export default function UnderlineTabs(props: Props) {
  const { showClientSearch, variant = 'default' } = props;
  const cq = useContainerQuery();
  const { flagEnabledResponsiveMenus } = useFlags();
  const tabsCollapsed =
    props.integrateBreadcrumb && flagEnabledResponsiveMenus && cq({ base: true, xs: false });
  const isCompactWidth = flagEnabledResponsiveMenus && cq({ base: true, sm: false });
  const showSearch = showClientSearch && !isCompactWidth;

  if (tabsCollapsed) {
    return <MobileCollapsedTabs {...props} />;
  }

  const tabsContent = (
    <Flex gap="md" wrap="nowrap" className={classes.scrollStrip}>
      {props.mode === 'router'
        ? props.tabs.map(tab => <RouterTabView key={tab.href} {...tab} />)
        : props.tabs.map(tab => (
            <LocalTabView
              key={tab.value}
              tab={tab}
              isActive={tab.value === props.value}
              onClick={() => props.onChange(tab.value)}
            />
          ))}
    </Flex>
  );

  const outerGap = cq({ base: 'xl', lg: 'xxl', xl: 'xxxl' });

  return (
    <Flex align="center" gap={outerGap} style={{ minWidth: 0, width: '100%' }}>
      {showSearch && <ClientSearch width={240} />}
      {variant === 'bordered' ? (
        <Flex
          style={{
            borderBottom: '1px solid var(--mantine-color-gray-2)',
            minWidth: 0,
            flex: 1,
          }}
        >
          {tabsContent}
        </Flex>
      ) : (
        tabsContent
      )}
    </Flex>
  );
}

function MobileCollapsedTabs(props: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [opened, setOpened] = useState(false);

  const resolvedActive = findActiveTab(props, pathname) ?? props.tabs[0];
  const activeLabel = resolvedActive?.label ?? '';
  const otherTabs = props.tabs.filter(tab => tab !== resolvedActive);

  const selectTab = (tab: RouterTab | LocalTab) => {
    setOpened(false);
    if (props.mode === 'router') {
      navigate((tab as RouterTab).href);
    } else {
      props.onChange((tab as LocalTab).value);
    }
  };

  return (
    <BreadcrumbTerminalSlotFill>
      <IconChevronRight size={16} />
      <Popover opened={opened} onChange={setOpened} position="bottom-end" shadow="md" withinPortal>
        <Popover.Target>
          <UnstyledButton
            onClick={() => setOpened(o => !o)}
            aria-haspopup="menu"
            aria-expanded={opened}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: 'var(--mantine-color-gray-10)',
              fontWeight: FONT_WEIGHTS.bold,
              fontSize: 'var(--mantine-font-size-md)',
            }}
          >
            {activeLabel}
            <IconChevronDown
              size={16}
              style={{
                transform: opened ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 150ms ease',
              }}
            />
          </UnstyledButton>
        </Popover.Target>

        <Popover.Dropdown p="xxs">
          <Stack gap="xxxs" role="menu">
            {otherTabs.map(tab => (
              <UnstyledButton
                key={props.mode === 'router' ? (tab as RouterTab).href : (tab as LocalTab).value}
                onClick={() => selectTab(tab)}
                role="menuitem"
                p="xs"
                style={{
                  borderRadius: 'var(--mantine-radius-sm)',
                  color: 'var(--mantine-color-gray-8)',
                  fontWeight: FONT_WEIGHTS.medium,
                  fontSize: 'var(--mantine-font-size-sm)',
                }}
              >
                {tab.label}
              </UnstyledButton>
            ))}
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </BreadcrumbTerminalSlotFill>
  );
}

function RouterTabView(props: RouterTab) {
  const { label, href } = props;
  const { pathname } = useLocation();
  const isActive = matchesRoute(props, pathname);
  const style = isActive
    ? {
        borderBottom: '2px solid var(--mantine-color-brand-6)',
        marginBottom: -1,
        color: 'black',
        fontWeight: FONT_WEIGHTS.bold,
      }
    : {
        color: 'var(--mantine-color-gray-8)',
        fontWeight: FONT_WEIGHTS.medium,
      };

  return (
    <NavLink to={href} style={{ textDecoration: 'none' }} className={classes.scrollTab}>
      <Text size="textSm" pb="xxs" px="xxxs" style={style}>
        {label}
      </Text>
    </NavLink>
  );
}

function LocalTabView({
  tab,
  isActive,
  onClick,
}: {
  tab: LocalTab;
  isActive: boolean;
  onClick: () => void;
}) {
  const textStyle = isActive
    ? {
        borderBottom: '2px solid var(--mantine-color-brand-6)',
        marginBottom: -1,
        color: 'black',
        fontWeight: FONT_WEIGHTS.medium,
      }
    : {
        color: 'var(--mantine-color-gray-8)',
        fontWeight: FONT_WEIGHTS.medium,
      };

  return (
    <button
      onClick={onClick}
      role="tab"
      aria-selected={isActive}
      type="button"
      className={classes.scrollTab}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        textDecoration: 'none',
      }}
    >
      <Text size="textSm" pb="xxs" px="xxxs" style={textStyle}>
        {tab.label}
      </Text>
    </button>
  );
}

function findActiveTab(props: Props, pathname: string): RouterTab | LocalTab | undefined {
  if (props.mode === 'router') {
    return props.tabs.find(tab => matchesRoute(tab, pathname));
  }
  return props.tabs.find(tab => tab.value === props.value);
}

function matchesRoute(tab: RouterTab, pathname: string): boolean {
  const matchHref = tab.matchHref || tab.href;
  const mode = tab.activeMatch ?? 'startsWith';
  if (mode === 'exact') return pathname === matchHref;
  if (mode === 'includes') return pathname.includes(matchHref);
  return pathname.startsWith(matchHref);
}
